import { PageHero } from '@components/common';
import { Server, ArrowRight, Shield } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

const idealFor = [
  'Organisations with complex cloud or hybrid infrastructure',
  'Companies experiencing rapid growth or cloud migration',
  'Businesses with compliance requirements (PCI-DSS, SOC 2, ISO 27001)',
  'Leadership teams concerned about technical security gaps',
  'Pre-acquisition technical due diligence',
  'Post-breach infrastructure hardening',
];

const deliverables = [
  {
    title: 'Infrastructure Assessment Report',
    description:
      'Comprehensive analysis of cloud and on-premise security controls, network architecture, and any vulnerabilities identified.',
  },
  {
    title: 'Remediation Roadmap',
    description: 'Prioritised action plan with effort estimates and quick wins',
  },
  {
    title: 'Technical Architecture Recommendations',
    description: 'Strategic guidance for secure infrastructure design',
  },
];

const process = [
  {
    step: '01',
    title: 'Scoping',
    description: 'Define infrastructure boundaries, access requirements, and focus areas',
    duration: '1 day',
  },
  {
    step: '02',
    title: 'Assessment',
    description: 'Automated scanning, manual configuration review, and architecture analysis',
    duration: '3-5 days',
  },
  {
    step: '03',
    title: 'Analysis',
    description: 'Risk assessment, prioritisation, and remediation planning',
    duration: '2-3 days',
  },
  {
    step: '04',
    title: 'Reporting',
    description: 'Findings presentation and remediation roadmap delivery',
    duration: '1-2 days',
  },
];

export default function TechnicalEstateReviewPage() {
  const packageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Technical Estate Review',
    description:
      'A thorough assessment of your cloud and on-premise infrastructure security. Identify vulnerabilities, misconfigurations, and technical debt before attackers do.',
    url: 'https://thecisonetwork.com/packages/technical-estate-review',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(packageSchema) }}
      />
      <PageHero
        title="Technical Estate Review"
        subtitle="A thorough assessment of your cloud and on-premise infrastructure security. Identify vulnerabilities, misconfigurations, and technical debt before attackers do."
        eyebrow="Our Packages"
        seoTitle="Technical Estate Review | The CISO Network"
        seoDescription="Comprehensive cloud and infrastructure security assessment. Identify vulnerabilities, misconfigurations, and get a prioritised remediation plan."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Understand Your Technical Security Posture
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Your infrastructure is the foundation of your security. Misconfigured cloud
                  services, excessive permissions, and unpatched systems are the entry points
                  attackers exploit.
                </p>
                <p>
                  Our Technical Estate Review provides a comprehensive assessment of your cloud and
                  on-premise infrastructure security. We go beyond automated scanning to understand
                  your architecture, identify vulnerabilities, and provide actionable remediation
                  guidance.
                </p>
                <p>
                  Whether you are preparing for compliance, concerned about gaps, or need to
                  validate your security posture, this review gives you clarity and a clear path
                  forward.
                </p>
              </div>
            </div>

            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Server className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">At a Glance</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Scope</span>
                  <span className="font-medium text-foreground">Cloud & On-Premise</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Deliverables</span>
                  <span className="font-medium text-foreground">Report + Roadmap</span>
                </div>
              </div>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full justify-center"
              >
                Discuss This Package <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Ideal For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {idealFor.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <Server className="h-5 w-5 text-teal" />
                </div>
                <p className="text-muted-foreground text-sm pt-2">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              What You Receive
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Clear, actionable deliverables that give you a complete picture of your technical
              security posture and a roadmap for improvement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {deliverables.map((item, index) => (
              <div
                key={index}
                className="bg-background rounded-2xl p-8 border border-border hover:border-teal/30 transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <Shield className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A structured, efficient process that minimises disruption while delivering
              comprehensive insights.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{step.step}</div>
                <h3 className="font-heading font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm mb-2">{step.description}</p>
                <span className="text-xs text-teal font-medium">{step.duration}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Understand Your Infrastructure Security?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a call to discuss your infrastructure and see if a Technical Estate Review would
            help identify and prioritise your security improvements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
